import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// -----------------------------------------------------------------------------
// `React.lazy` that survives a stale chunk.
//
// A lazy screen's `import()` fails with "Failed to fetch dynamically imported
// module" when the URL it was compiled against no longer exists:
//
//   - in DEV, when Vite discovers a new dependency mid-session and re-bundles
//     its deps - every module graph the open tab holds is now outdated;
//   - in PRODUCTION, after a deploy - the old content-hashed chunk is gone and
//     a tab opened before the deploy still asks for it.
//
// Either way a page reload fetches a fresh graph and the import succeeds. So the
// first such failure reloads once; a second within the window is a real outage
// and is rethrown to the route's error element rather than looping.
// -----------------------------------------------------------------------------

const RELOAD_KEY = 'sectfit-admin:chunk-reload-at';
const RELOAD_WINDOW_MS = 10_000;

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk .* failed/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    return Date.now() - at < RELOAD_WINDOW_MS;
  } catch {
    // Storage blocked: never auto-reload, a loop is worse than an error screen.
    return true;
  }
}

export function lazyRoute<T extends ComponentType<object>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && !reloadedRecently()) {
        try {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
          // Unreachable in practice - reloadedRecently() already returned true.
        }
        window.location.reload();
        // Keep Suspense showing its fallback until the reload takes over.
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
